/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { type ReactElement, useCallback } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import { MuiFade, breakpoints } from '@coursera/cds-core';
import { ArrowDownIcon, ArrowUpIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import ChatFooter from 'bundles/ai-coach-platform/components/patterns/chat/ChatFooter';
import ChatInputField from 'bundles/ai-coach-platform/components/patterns/chat/ChatInputField';
import ChatMessages, {
  type Props as ChatMessagesProps,
  type HideMessageType,
} from 'bundles/ai-coach-platform/components/patterns/chat/ChatMessages';
import {
  ChatHeader,
  ChatHistoryLoading,
  ChatRichInputField,
} from 'bundles/ai-coach-platform/components/patterns/chat/common';
import type { ChatPopupButton } from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatHeader';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useDynamicContent } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useDynamicContent';
import { useEarlierMessagesButton } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useEarlierMessagesButton';
import { useJumpToBottom } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useJumpToBottom';
import { useScrollPositionPreservation } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useScrollPositionPreservation';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { AnyFunction, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { CoachMode } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

import store from './store';

const styles = {
  conversationContainer: css`
    height: 100%;
  `,
  conversation: css`
    background-color: var(--cds-color-neutral-background-primary);
    height: 100%;
    width: 100%;
    display: flex;
    flex-flow: column;
    z-index: 10;
    border: none;
    border-radius: var(--cds-border-radius-200);
    position: relative;
    overflow-y: hidden; // this hides the overflowing transform on dynamic composer content

    ${breakpoints.down('xs')} {
      border-radius: 0;
    }

    &.active {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }

    &.hidden {
      z-index: -1;
      pointer-events: none;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }
  `,
  headerContainer: css`
    flex-grow: 0;
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  messageContainer: css`
    flex-grow: 1;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    overflow-x: hidden;
    justify-content: center;
    max-width: 100%;
    position: relative;
  `,
  messageContainerResponses: css`
    max-width: 770px;
    margin: 0 auto;
  `,
  composerContainer: css`
    flex-grow: 0;
    width: 100%;
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    transition: transform 0.4s ease-in-out;

    &.hidden {
      transform: translate3d(0, 100%, 0);
    }
  `,
  composer: css`
    max-width: 770px;
    margin: 0 auto;
  `,
  footerContainer: (hideComposer = false) => css`
    flex-grow: 0;
    margin-top: 2px;
    background: var(--cds-color-neutral-background-primary);
    z-index: 1;
    display: flex;
    justify-content: center;

    ${hideComposer && 'border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);'}
  `,
  footer: css`
    max-width: 770px;
  `,
  jumpButton: css`
    position: sticky;
    bottom: var(--cds-spacing-100);
    left: 50%;
    transform: translateX(-50%) scale(1);
    z-index: 10;
    background-color: var(--cds-color-neutral-background-primary);
    box-shadow: 0 0 4px 0 var(--cds-color-darken-200); /* TODO:replace with CDS "elevation 2" once available */
    outline: 1px solid var(--cds-color-interactive-background-secondary-hover-weak);

    &:hover {
      background-color: var(--cds-color-interactive-background-secondary-hover-weak);
    }

    &:active {
      transform: translateX(-50%) scale(0.98);
    }
  `,
  earlierMessagesButton: css`
    position: absolute;
    top: var(--cds-spacing-800);
    left: 50%;
    transform: translateX(-50%) scale(1);
    z-index: 10;
    background-color: var(--cds-color-neutral-background-primary);
    box-shadow: 0 0 4px 0 var(--cds-color-darken-200);
    outline: 1px solid var(--cds-color-interactive-background-secondary-hover-weak);

    &:hover {
      background-color: var(--cds-color-interactive-background-secondary-hover-weak);
    }

    &:active {
      transform: translateX(-50%) scale(0.98);
    }
  `,
};

export type ConversationProps = {
  sendMessage: SendMessageFunc;
  toggleChat: AnyFunction;
  showStartNewChat?: boolean;
  emptyState?: ReactElement;
  senderPlaceHolder?: string;
  inputLabel?: string;
  showCloseButton?: boolean;
  disableInput?: boolean;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
  autofocus?: boolean;
  activeChatSessionId?: string;
  onTextInputChange?: (text: string) => void;
  handleNewSession?: () => void;
  isChatHistoryLoading?: boolean;
  customHeader?: ReactElement | null;
  customFooter?: ReactElement | null;
  hideFooter?: boolean;
  hideMessage?: HideMessageType;
  showSettings?: boolean;
  handleSettingsClick?: () => void;
  isCoachStudio?: boolean;
  showFullScreenButton?: boolean;
  toggleFullScreen?: () => void;
  isFullScreen?: boolean;
  showPositionToggle?: boolean;
  handlePositionToggleClick?: () => void;
  chatPopupButton?: ChatPopupButton;
  handlePopupButtonClick?: () => void;
  enableRichInputField?: boolean;
  renderCustomInputField?: ({ sendMessage }: { sendMessage: SendMessageFunc }) => ReactElement | null;
  renderCustomMessage?: ChatMessagesProps['renderCustomMessage'];
  renderCustomMessageLoader?: ChatMessagesProps['renderCustomMessageLoader'];
  renderCustomTemporaryStreamedMessage?: ChatMessagesProps['renderCustomTemporaryStreamedMessage'];
  chatMessageListAriaLabel?: string;
  renderTemporaryMessage?: () => ReactElement | null;
  showDynamicComposerContent?: boolean;
  dynamicComposerContent?: ReactElement | null;
  onDynamicComposerContentShown?: () => void;
  onDefaultComposerContentShown?: () => void;
  enableTextStreaming?: boolean;
  showSearchButton?: boolean;
  hideComposer?: boolean;
  searchButtonRef?: React.RefObject<HTMLButtonElement>;
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  enableContinuousChat?: boolean;
  onFetchMoreChatHistory?: () => void;
};

/**
 * Standalone component that handles all the conversations with Coach.
 */
const Conversation = ({
  senderPlaceHolder,
  inputLabel,
  showCloseButton = true,
  disableInput = false,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
  autofocus = true,
  showStartNewChat = true,
  sendMessage,
  toggleChat,
  activeChatSessionId,
  onTextInputChange,
  handleNewSession,
  emptyState,
  isChatHistoryLoading = false,
  customHeader,
  customFooter,
  hideFooter = false,
  hideMessage,
  showSettings = false,
  handleSettingsClick = () => {},
  isCoachStudio = false,
  showFullScreenButton = false,
  toggleFullScreen,
  isFullScreen = false,
  showPositionToggle,
  handlePositionToggleClick,
  chatPopupButton,
  handlePopupButtonClick,
  enableRichInputField = false,
  renderCustomInputField,
  renderCustomMessage,
  renderCustomMessageLoader,
  renderCustomTemporaryStreamedMessage,
  renderTemporaryMessage,
  chatMessageListAriaLabel,
  showDynamicComposerContent = false,
  dynamicComposerContent = null,
  onDynamicComposerContentShown = () => null,
  onDefaultComposerContentShown = () => null,
  enableTextStreaming = false,
  showSearchButton = false,
  hideComposer = false,
  searchButtonRef,
  settingsButtonRef,
  enableContinuousChat = false,
  onFetchMoreChatHistory,
}: ConversationProps) => {
  const { isMessageLoading, isMessageStreaming, messagesCount } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
    messagesCount: state.messages.messages.length,
  }));
  const composerContainerRef = React.useRef(null);
  const messageContainerRef = React.useRef<HTMLDivElement | null>(null);
  const {
    isSearchMode,
    searchTerm,
    suggestedActions,
    earlierMessagesAvailable,
    chatHistoryCursor,
    chatHistoryLimit,
    chatMode,
  } = useCoachChatContext();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const { sessionState } = store.getState();
  const { continuousChatSessionId } = sessionState;

  const defaultComposerComponent = React.useMemo(
    () => (
      <>
        {renderCustomInputField?.({ sendMessage }) ??
          (enableRichInputField ? (
            <ChatRichInputField
              sendMessage={sendMessage}
              placeholder={senderPlaceHolder || _t('Ask me anything')}
              disableInput={disableInput}
              disabled={isMessageLoading || isMessageStreaming}
              disableSpeechToText={disableSpeechToText}
              speechToTextDisabledTooltip={speechToTextDisabledTooltip}
              autofocus={autofocus}
              // only show suggested actions on non-empty chats because we have EmptyState for that case already
              suggestedActions={messagesCount > 0 ? suggestedActions : undefined}
            />
          ) : (
            <ChatInputField
              inputLabel={inputLabel}
              sendMessage={sendMessage}
              placeholder={senderPlaceHolder || _t('Ask me anything')}
              disableInput={disableInput}
              autofocus={autofocus}
              onTextInputChange={onTextInputChange}
            />
          ))}
      </>
    ),
    [
      renderCustomInputField,
      sendMessage,
      enableRichInputField,
      senderPlaceHolder,
      disableInput,
      disableSpeechToText,
      speechToTextDisabledTooltip,
      autofocus,
      onTextInputChange,
      isMessageLoading,
      isMessageStreaming,
      suggestedActions,
      messagesCount,
      inputLabel,
    ]
  );

  const { composerComponent, composerHidden, onTransitionEnd } = useDynamicContent({
    defaultComposerComponent,
    dynamicComposerContent,
    showDynamicComposerContent,
    onDynamicComposerContentShown,
    onDefaultComposerContentShown,
  });

  const handleTransitionEnd = React.useCallback<(e: React.TransitionEvent) => void>(
    (e: React.TransitionEvent) => {
      // scope transitionEnd event to current container to avoid any bubbling events
      // to trigger this call
      if (e.target === composerContainerRef.current) {
        onTransitionEnd();
      }
    },
    [onTransitionEnd]
  );

  const { showJumpButton, scrollToBottom, handleScroll, isAtBottom } = useJumpToBottom({
    containerRef: messageContainerRef,
    messageCount: messagesCount,
    isMessageLoading: isMessageLoading || isMessageStreaming,
    enabled: enableTextStreaming || isSearchMode(),
  });

  const { showEarlierMessagesButton, setShowEarlierMessagesButton, setOldestMessageRef } = useEarlierMessagesButton({
    earlierMessagesAvailable,
  });

  const earlierMessagesButtonTrackingData = React.useMemo(
    () =>
      chatHistoryCursor
        ? {
            // Cursor is a string that represents the current number of messages in the chat
            // We convert it to a number that represents the current "page" by dividing by the number of messages on one "page"
            pagination: {
              pageNumber: parseInt(chatHistoryCursor, 10) / chatHistoryLimit,
              pageSize: chatHistoryLimit,
            },
            coach: {
              chatSessionId: continuousChatSessionId,
            },
          }
        : null,
    [chatHistoryCursor, chatHistoryLimit, continuousChatSessionId]
  );

  const shouldShowEarlierMessagesButton =
    enableContinuousChat && !isSearchMode() && messagesCount > 0 && showEarlierMessagesButton;

  React.useEffect(() => {
    // Tracking visibility of the "Earlier messages" button this way because useVisibilityTracker does not work as expected due the button always being in the DOM with MuiFade
    if (shouldShowEarlierMessagesButton && earlierMessagesButtonTrackingData) {
      track('view_coach_earlier_messages', earlierMessagesButtonTrackingData);
    }
  }, [shouldShowEarlierMessagesButton, earlierMessagesButtonTrackingData, track]);

  const { captureScrollState, restoreScrollPosition } = useScrollPositionPreservation(messageContainerRef);

  // Track message count to detect when new messages are prepended
  const previousMessageCount = React.useRef(messagesCount);

  // Restore scroll position after new messages are prepended (when enableContinuousChat is true)
  React.useEffect(() => {
    if (enableContinuousChat && messagesCount > previousMessageCount.current) {
      // Use a small delay to ensure DOM has been updated
      requestAnimationFrame(() => {
        restoreScrollPosition();
      });
    }
    previousMessageCount.current = messagesCount;
  }, [messagesCount, enableContinuousChat, restoreScrollPosition]);

  const handleJumpToTop = useCallback<() => void>(() => {
    messageContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    track('interact_coach_chat_search', {
      coachChatSearchInteractionType: 'click_jump_to_top',
      coachChatSearchQuery: searchTerm ?? '',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
      },
    });
  }, [track, trackingContext?.mode, searchTerm]);

  const handleJumpToBottom = () => {
    track('interact_coach', {
      coachInteractionType: 'click_jump_to_bottom',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
        location: chatMode === 'conversation' ? 'chat' : 'chat_search',
      },
    });

    scrollToBottom();
  };

  const handleClickEarlierMessagesButton = useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    (e) => {
      // We need to update the showEarlierMessagesButton state to false so that we don't fire the view_coach_earlier_messages event unexpectedly when the "Earlier Messages" button is clicked and
      // before the oldest message ref is updated to the now oldest message that is fetched.
      // If there are more earlier messages, the state will be updated to true from the onFetchMoreChatHistory call.
      setShowEarlierMessagesButton(false);

      // Capture current scroll state before fetching more messages,
      // so we can preserve user's scroll position
      captureScrollState();
      onFetchMoreChatHistory?.();

      if (earlierMessagesButtonTrackingData) {
        track('click_coach_earlier_messages', earlierMessagesButtonTrackingData);
      }
    },
    [onFetchMoreChatHistory, captureScrollState, track, earlierMessagesButtonTrackingData, setShowEarlierMessagesButton]
  );

  return (
    <div css={styles.conversation} className="coach-conversation-container">
      {/*
        If customHeader is undefined we show the default header.
        If a React element is passed for customHeader, the custom header is displayed.
        If customHeader is null, we hide the header.
      */}
      {customHeader === undefined && (
        <div css={styles.headerContainer}>
          <ChatHeader
            onClose={toggleChat}
            showStartNewChat={showStartNewChat}
            showClose={showCloseButton}
            handleNewSession={handleNewSession}
            handleSettingsClick={handleSettingsClick}
            showSettings={showSettings}
            loading={isChatHistoryLoading || isMessageLoading || isMessageStreaming}
            isCoachStudio={isCoachStudio}
            showFullScreenButton={showFullScreenButton}
            toggleFullScreen={toggleFullScreen}
            isFullScreen={isFullScreen}
            showPositionToggle={showPositionToggle}
            handlePositionToggleClick={handlePositionToggleClick}
            chatPopupButton={chatPopupButton}
            handlePopupButtonClick={handlePopupButtonClick}
            showSearchButton={messagesCount > 0 ? showSearchButton : false}
            searchButtonRef={searchButtonRef}
            settingsButtonRef={settingsButtonRef}
            enableContinuousChat={enableContinuousChat}
          />
        </div>
      )}
      {customHeader !== undefined && customHeader !== null && <div css={styles.headerContainer}>{customHeader}</div>}

      <MuiFade in={shouldShowEarlierMessagesButton} timeout={200}>
        <CoachButton
          aria-label={_t('Earlier messages')}
          icon={<ArrowUpIcon />}
          iconPosition="before"
          onClick={handleClickEarlierMessagesButton}
          css={styles.earlierMessagesButton}
          size="small"
          variant="ghost"
          data-testid="earlier-messages-button"
        >
          {_t('Earlier messages')}
        </CoachButton>
      </MuiFade>

      <div css={styles.messageContainer} ref={messageContainerRef} onScroll={handleScroll}>
        <div css={styles.messageContainerResponses}>
          {isChatHistoryLoading && <ChatHistoryLoading />}
          {!isChatHistoryLoading && (
            <ChatMessages
              emptyState={emptyState}
              sendMessage={sendMessage}
              hideMessage={hideMessage}
              activeChatSessionId={activeChatSessionId}
              renderCustomMessage={renderCustomMessage}
              renderCustomMessageLoader={renderCustomMessageLoader}
              renderCustomTemporaryStreamedMessage={renderCustomTemporaryStreamedMessage}
              renderTemporaryMessage={renderTemporaryMessage}
              enableTextStreaming={enableTextStreaming}
              enableContinuousChat={enableContinuousChat}
              setOldestMessageRef={setOldestMessageRef}
              aria-label={chatMessageListAriaLabel}
            />
          )}
        </div>
        <MuiFade in={isSearchMode() && isAtBottom} timeout={200}>
          <CoachButton
            aria-label={_t('Jump to top')}
            icon={<ArrowUpIcon />}
            iconPosition="before"
            onClick={handleJumpToTop}
            css={styles.jumpButton}
            size="small"
            variant="ghost"
            data-testid="jump-to-top-button"
          >
            {_t('Top')}
          </CoachButton>
        </MuiFade>
        <MuiFade in={messagesCount > 0 && showJumpButton} timeout={200}>
          <CoachButton
            aria-label={_t('Jump to bottom')}
            icon={<ArrowDownIcon />}
            iconPosition="before"
            onClick={handleJumpToBottom}
            css={styles.jumpButton}
            size="small"
            variant="ghost"
            data-testid="jump-to-bottom-button"
          >
            {_t('Bottom')}
          </CoachButton>
        </MuiFade>
      </div>

      {!hideComposer && (
        <div
          ref={composerContainerRef}
          css={styles.composerContainer}
          onTransitionEnd={handleTransitionEnd}
          className={classNames({ hidden: composerHidden })}
        >
          <div css={styles.composer}>{composerComponent}</div>
        </div>
      )}
      {!hideFooter && (
        <div css={styles.footerContainer(hideComposer)} className="coach-conversation-footer-container">
          <div css={styles.footer}>{customFooter || <ChatFooter />}</div>
        </div>
      )}
    </div>
  );
};

export default Conversation;
